<template>
    <loading-spinner v-if="loading"/>
    <x-data-table
        v-else
        class="no-row-hover"
        :headers="headers"
        :items="amendments"
        hide-actions
    >
        <template v-slot:no-data>
            <p class="error-message text-xs-center">No amendments made</p>
        </template>

        <template v-slot:items="props">
            <tr>
                <td>{{ props.item.effective_date | formatDate }}</td>
                <td>{{ props.item.reference }}</td>
                <td>{{ props.item.type | snakeToTitle }}</td>
                <td>
                    Request: {{ props.item.type | snakeToTitle }}
                    <div v-if="props.item.settlement_due_date">
                        Settlement due date: {{ props.item.settlement_due_date | formatDate }}
                    </div>
                </td>
                <td>N/A</td>
                <td><status-chip small :status="getStatus(props.item.status)"/></td>
                <td class="text-xs-right">
                    <template v-if="showModalReview(props.item.status)">
                        <x-dialog v-model="dialog[props.item.id]" persistent width="900" max-width="900">
                            <template v-slot:activator="{ on }">
                                <x-btn color="tertiary" small v-on="on" @click.stop="$set(dialog, props.item.id, true)">Review</x-btn>
                            </template>

                            <x-container v-if="isOffRisk(props.item)" fluid grid-list-lg>
                                <x-layout>
                                    <x-flex xs12>
                                        <h2>Off risk confirmation</h2>

                                        <h3>Final premium confirmation</h3>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex xs12>
                                        <template v-if="myFinalPremiumLines.length > 1">
                                            <p v-for="line in myFinalPremiumLines" :key="line.id">
                                                Final premium — {{ line.label }}: {{ line.final_premium | currency(declaration.currency.code) }}
                                            </p>
                                            <p>Total final premium: {{ myFinalPremium | currency(declaration.currency.code) }}</p>
                                        </template>

                                        <p v-else>Final premium: {{ myFinalPremium | currency(declaration.currency.code) }}</p>

                                        <p>Off risk date: {{ props.item.effective_date | formatDate }}</p>
                                        <p v-if="props.item.settlement_due_date">Settlement due date: {{ props.item.settlement_due_date | formatDate }}</p>
                                    </x-flex>
                                </x-layout>

                                <x-layout my-3>
                                    <x-flex xs12>
                                        <document-list :documentable-id="declaration.id" documentable-type="declaration" :documentable-category-id="2" :can-delete="() => false"/>
                                    </x-flex>
                                </x-layout>

                                <x-form ref="amendmentForm">
                                    <x-layout class="response-actions" justify-space-between>
                                        <x-flex xs6>
                                            <x-btn class="ma-0" color="tertiary" small :disabled="!idle" @click.stop="$set(dialog, props.item.id, false)">Cancel</x-btn>
                                        </x-flex>

                                        <x-flex xs6 class="text-xs-right">
                                            <x-btn class="ma-0" :disabled="!idle" :loading="declining" color="tertiary" small @click="decline()">Decline</x-btn>

                                            <x-btn class="ma-0 ml-2" :disabled="!idle" :loading="accepting" color="primary" small @click="accept()">Agree</x-btn>
                                        </x-flex>
                                    </x-layout>
                                </x-form>
                            </x-container>

                            <x-container v-else>
                                <amendment-review-list
                                    v-if="pendingAmendment"
                                    :declaration="declaration"
                                >
                                    <x-form ref="amendmentForm">
                                        <template v-if="myLinesRequiringReference.length > 0">
                                            <x-layout v-for="(referenceLine, referenceLineIndex) in myLinesRequiringReference" :key="referenceLine.id">
                                                <x-flex xs12>
                                                    <x-text-field
                                                        v-model="underwriterReferences[referenceLine.id]"
                                                        :label="referenceLabel(referenceLine, referenceLineIndex)"
                                                        placeholder=" "
                                                        :rules="[v => ruleRequired(v, 'Underwriter reference')]"
                                                        outline
                                                    />
                                                </x-flex>
                                            </x-layout>

                                            <x-layout v-if="isNewInsurer">
                                                <x-flex xs12>
                                                    <x-select
                                                        v-model="riskCode"
                                                        :items="riskCodes"
                                                        item-text="name"
                                                        item-value="name"
                                                        label="Risk code"
                                                        placeholder="Risk code"
                                                        :rules="[v => ruleRequired(v, 'Risk code')]"
                                                        append-icon="keyboard_arrow_down"
                                                        outline
                                                        :menu-props="{
                                                            offsetY: true
                                                        }"
                                                    />
                                                </x-flex>
                                            </x-layout>
                                        </template>
                                    </x-form>

                                    <x-layout>
                                        <x-flex xs6>
                                            <x-btn color="tertiary" :disabled="!idle" @click.stop="$set(dialog, props.item.id, false)">Cancel</x-btn>
                                        </x-flex>

                                        <x-flex xs6 text-xs-right>
                                            <x-btn color="tertiary" :disabled="!idle" :loading="declining" @click="decline()">Decline changes</x-btn>

                                            <x-btn color="primary" class="ml-2" :disabled="!idle" :loading="accepting" @click="accept()">Accept changes</x-btn>
                                        </x-flex>
                                    </x-layout>
                                </amendment-review-list>
                            </x-container>
                        </x-dialog>
                    </template>

                    <template v-if="showViewDocumentsModal(props.item)">
                        <x-dialog v-model="dialog[props.item.id]" width="900" max-width="900">
                            <template v-slot:activator="{ on }">
                                <x-btn color="tertiary" small v-on="on">View documents</x-btn>
                            </template>

                            <x-container>
                                <document-list
                                    v-if="props.item.id"
                                    :documentable-id="props.item.id"
                                    documentable-type="endorsement"
                                    :can-delete="() => false"
                                />
                            </x-container>
                        </x-dialog>
                    </template>
                </td>
            </tr>
        </template>
    </x-data-table>
</template>

<script>
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';
    import StatusChip from "~/components/Shared/UIComponents/StatusChip";
    import AmendmentReviewList from "~/components/Shared/UIComponents/AmendmentReviewList";
    import rules from '~/mixins/rules';

    export default {
        name: "AmendmentsTable",
        mixins: [rules],
        components: {
            DocumentList,
            AmendmentReviewList,
            StatusChip
        },
        data: () => ({
            status: 'idle',
            dialog: {},
            headers: [
                {
                    text: 'Effective Date',
                    value: 'effective_date',
                    width: '10%',
                },
                {
                    text: 'Reference',
                    value: 'reference',
                    width: '5%',
                },
                {
                    text: 'Type',
                    value: 'type'
                },
                {
                    text: 'Description',
                    value: 'type'
                },
                {
                    text: 'Premium change',
                    value: ''
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false,
                    width: '10%',
                },
                {
                    text: 'Actions',
                    sortable: false,
                    align: 'right',
                    width: '20%',
                }
            ],
            changeHeaders: [
                {
                    text: 'Field',
                    value: 'field'
                },
                {
                    text: 'Old value',
                    value: 'old_value'
                },
                {
                    text: 'New value',
                    value: 'new_value'
                }
            ],
            underwriterReferences: {},
            riskCode: null,
            riskCodes: [],
            loading: true,
            endorsementDeclarations: [],
        }),
        props: {
            declaration: {
                type: Object,
                default: []
            },
            isCaptiveDeclaration: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            endorsements() {
                return this.declaration.endorsements;
            },
            endorsementRequest() {
                return this.declaration.endorsement_request;
            },
            amendments() {
                return this.endorsements.filter((endorsement) => ["amendment", "off_risk"].includes(endorsement.type));
            },
            pendingAmendment() {
                return this.endorsements.find(endorsement => endorsement.type === 'amendment' && endorsement.status === 'pending');
            },
            pendingOffRisk() {
                return this.endorsements.find(endorsement => endorsement.type === 'off_risk' && endorsement.status === 'pending');
            },
            endorsementRequestStatus() {
                if (this.endorsementRequest === null) {
                    const allMyLinesOffRisk = this.myDeclarationLines.length > 0 &&
                        this.myDeclarationLines.every(line => line.line_percent === 0);

                    return allMyLinesOffRisk ? 'off_risk_amendment' : 'approved';
                }
                return this.endorsementRequest.status;
            },
            idle() {
                return this.status === 'idle';
            },
            accepting() {
                return this.status === 'accepting';
            },
            declining() {
                return this.status === 'declining';
            },
            myFinalPremium() {
                return this.myFinalPremiumLines.reduce((sum, line) => sum + (parseFloat(line.final_premium) || 0), 0);
            },
            myFinalPremiumLines() {
                const myLines = (this.endorsementRequest?.endorsement?.field_changes?.lines || [])
                    .filter(line => (line.insurer_company_id === this.$root.user.company_id || line.captive_insurer_company_id === this.$root.user.company_id));

                return myLines.map(line => {
                    const declarationLine = (this.declaration.lines || []).find(({id}) => id === line.id);

                    return {
                        ...line,
                        label: declarationLine && declarationLine.security ? declarationLine.security.name : 'No security',
                    };
                });
            },
            myNewEndorsementLines() {
                return (this.endorsementDeclaration?.lines || [])
                    .filter(line => !line.original_line_id && line.insurer_company_id === this.$root.user.company_id);
            },
            mySecurityChangedLines() {
                return (this.endorsementDeclaration?.lines || []).filter(line => {
                    if (!line.original_line_id || line.insurer_company_id !== this.$root.user.company_id) {
                        return false;
                    }

                    const originalLine = this.myDeclarationLines.find(({id}) => id === line.original_line_id);

                    return Boolean(originalLine) && originalLine.security_id !== line.security_id;
                });
            },
            myLinesRequiringReference() {
                return [...this.myNewEndorsementLines, ...this.mySecurityChangedLines];
            },
            isNewInsurer() {
                return this.myNewEndorsementLines.length > 0;
            },
            myDeclarationLines() {
                return (this.declaration.lines || []).filter(line => (line.insurer_company_id === this.$root.user.company_id));
            },
            declarationLine() {
                return this.myDeclarationLines[0];
            },
            endorsementDeclaration() {
                // Off risk endorsements have no pending amendment to match against.
                if (!this.pendingAmendment) {
                    return null;
                }

                return this.endorsementDeclarations.find(ed => {
                    return this.pendingAmendment.id === ed.endorsement_id;
                });
            }
        },
        watch: {
            myLinesRequiringReference: {
                immediate: true,
                handler(lines) {
                    const references = {};
                    (lines || []).forEach(line => {
                        references[line.id] = this.underwriterReferences[line.id] || null;
                    });
                    this.underwriterReferences = references;
                }
            }
        },
        created() {
            this.$eventBus.$on('action-review-amendment', () => { this.openAmendmentReview() });
            this.$eventBus.$on('action-review-off-risk', () => { this.openOffRiskReview() });

            this.fetchData();
        },
        methods: {
            getStatus(status) {
                if (status === 'pending') {
                    return this.endorsementRequestStatus;
                }

                return status;
            },
            showModalReview(status) {
                if (this.isCaptiveDeclaration && status === 'captive_approved') {
                    return false;
                }

                if (status !== 'pending' || ['approved', 'off_risk_amendment'].includes(this.endorsementRequestStatus)) {
                    return false;
                }

                return true;
            },
            referenceLabel(line, index) {
                if (this.myLinesRequiringReference.length <= 1) {
                    return 'Underwriter reference';
                }

                const securityName = line.security ? line.security.name : `line ${index + 1}`;

                return `Underwriter reference — ${securityName}`;
            },
            async accept() {
                if (this.$refs.amendmentForm.validate()) {
                    this.status = 'accepting';
                    const references = this.myLinesRequiringReference.map(line => ({
                        endorsement_line_id: line.id,
                        underwriter_reference: this.underwriterReferences[line.id]
                    }));

                    await axios.put(this.$root.api(`insurer/endorsement/${this.endorsementRequest.endorsement_id}/accept`), {
                        underwriter_reference: references.length > 0 ? references[0].underwriter_reference : null,
                        underwriter_references: references,
                        risk_code: this.riskCode
                    });
                    this.$router.push({name: 'insurer-dashboard'});
                }
            },
            async decline() {
                this.status = 'declining';
                await axios.put(this.$root.api(`insurer/endorsement/${this.endorsementRequest.endorsement_id}/decline`));
                this.$router.push({name: 'insurer-dashboard'});
            },
            isOffRisk(endorsement) {
                return endorsement.type === 'off_risk';
            },
            openAmendmentReview() {
                this.$set(this.dialog, this.pendingAmendment.id, true);
            },
            openOffRiskReview() {
                this.$set(this.dialog, this.pendingOffRisk.id, true);
            },
            async fetchData() {
                const dataRequests = [];

                dataRequests.push(
                    ...[
                        axios.get(this.$root.api('risk-codes')).then(res => {
                            this.riskCodes = res.data.data;
                        }),
                        axios.get(this.$root.api(`endorsement-declaration/${this.declaration.id}`)).then(res => {
                            this.endorsementDeclarations = res.data.data;
                        })
                    ]
                );

                axios.all(dataRequests).then(() => {
                    this.loading = false;
                });
            },
            showViewDocumentsModal(endorsement) {
                return ['approved', 'applied'].includes(endorsement.status);
            }
        },
        beforeDestroy() {
            this.$eventBus.$off('action-review-amendment');
            this.$eventBus.$off('action-review-off-risk');
        },
    }
</script>
